import axios from 'axios';
import { isEmpty } from 'lodash';
import snakecaseKeys from 'snakecase-keys';

import { postify, stringify } from 'helpers/query_string_helpers';
import { setItem } from 'helpers/session_storage_helper';
import { propertyTypeParams } from 'helpers/research/filter_helpers';

import debounceActionCreators from 'helpers/redux/debounce_action_creators';
import RansackQuery from 'helpers/ransack_query';
import { buildQueryParams as buildCustomFieldQueryParams } from 'bundles/apex/custom_fields/filters/helpers';
import { hideMapInfoBubble } from './ui';
import { propertyFromApi } from '../../../property_api';
import { leaseRateMappings } from '../../research_constants';

const debouncedActionCreators = debounceActionCreators({ retrieveRecords }, 1000);

const keys = {
  assetClasses: 'q[property_type_in]',
  availabilities: 'q[with_listing_status]',
  buildingClasses: 'q[building_class_in]',
  buildingNumber: 'q[building_number]',
  cities: 'q[city_in]',
  county: 'q[county_cont]',
  databaseId: 'q[database_id_eq]',
  excludeTags: 'exclude_tags',
  investmentTypes: 'q[investment_type_in]',
  maxAvailableSF: 'q[available_sf_lteq]',
  market: 'q[market_eq]',
  microMarket: 'q[micro_market_eq]',
  maxBuildingSize: 'q[building_size_lteq]',
  maxCeilingHeight: 'q[ceiling_height_lteq]',
  maxMortgageAmount: 'q[mortgage_amount_lteq]',
  maxMortgageRecordedDate: 'q[mortgage_recorded_date_lteq]',
  maxNumKeys: 'q[number_of_units_lteq]',
  maxNumUnits: 'q[number_of_units_lteq]',
  maxYearBuilt: 'q[year_built_lteq]',
  minActiveDealCount: 'q[active_deal_count_gteq]',
  minActiveEdgeProjectCount: 'q[active_edge_project_count_gteq]',
  minAvailableSF: 'q[available_sf_gteq]',
  minBuildingSize: 'q[building_size_gteq]',
  minMortgageAmount: 'q[mortgage_amount_gteq]',
  minMortgageRecordedDate: 'q[mortgage_recorded_date_gteq]',
  minNumKeys: 'q[number_of_units_gteq]',
  minNumUnits: 'q[number_of_units_gteq]',
  minYearBuilt: 'q[year_built_gteq]',
  minCeilingHeight: 'q[ceiling_height_gteq]',
  propertyPage: 'property_page',
  propertySort: 'q[s]',
  researchGroupIds: 'q[research_groups_id_in]',
  searchText: 'search_text',
  state: 'q[state_cont]',
  street: 'q[address_cont]',
  subMarket: 'q[submarket_eq]',
  tags: 'with_all_tags',
  zip: 'q[zip_cont]',
};

const saleAndLeaseKeys = {
  maxSalesPrice: 'q[sales_price_lteq]',
  minSalesPrice: 'q[sales_price_gteq]',
  maxSalesDate: 'q[sales_sale_date_lteq]',
  minSalesDate: 'q[sales_sale_date_gteq]',
  maxLeaseDate: 'q[leases_lease_date_lteq]',
  minLeaseDate: 'q[leases_lease_date_gteq]',
  maxLeaseSize: 'q[leases_size_lteq]',
  minLeaseSize: 'q[leases_size_gteq]',
  maxSfPrice: 'q[sales_price_per_sf_lteq]',
  minSfPrice: 'q[sales_price_per_sf_gteq]',
  maxUnitPrice: 'q[sales_price_per_unit_lteq]',
  minUnitPrice: 'q[sales_price_per_unit_gteq]',
  maxCapRate: 'q[sales_cap_rate_pct_lteq]',
  minCapRate: 'q[sales_cap_rate_pct_gteq]',
  leaseSpaceType: 'q[leases_space_type_id_eq]',
  maxLeaseExecutionDate: 'q[leases_lease_execution_date_lteq]',
  minLeaseExecutionDate: 'q[leases_lease_execution_date_gteq]',
  maxLeaseExpirationDate: 'q[leases_lease_expiration_date_lteq]',
  minLeaseExpirationDate: 'q[leases_lease_expiration_date_gteq]',
  leaseType: 'q[leases_lease_type_id_eq]'
};

const  multifamilyKeys = {
  maxBeds: 'q[unit_mix_entries_bedrooms_lteq]',
  minBeds: 'q[unit_mix_entries_bedrooms_gteq]',
  maxBaths: 'q[unit_mix_entries_bathrooms_lteq]',
  minBaths: 'q[unit_mix_entries_bathrooms_gteq]',
  maxSize: 'q[unit_mix_entries_size_lteq]',
  minSize: 'q[unit_mix_entries_size_gteq]',
  maxRent: 'q[unit_mix_entries_rent_lteq]',
  minRent: 'q[unit_mix_entries_rent_gteq]',
  maxMarketRent: 'q[unit_mix_entries_market_rent_lteq]',
  minMarketRent: 'q[unit_mix_entries_market_rent_gteq]'
};

const industrialKeys  = {
  availableRailProvider: 'q[available_rail_provider_eq]',
  driveThroughAccess: 'q[drive_through_access_eq]',
  lotBorderType: 'q[lot_border_type_eq]',
  lotStabilization: 'q[lot_stabilization_eq]',
  maxAcresStabilized: 'q[min_yard_lteq]',
  maxBridgeCraneSpan: 'q[max_bridge_crane_span_lteq]',
  maxJibCraneSpan: 'q[jib_crane_span_lteq]',
  maxNumBridgeCranes: 'q[number_of_bridge_cranes_lteq]',
  maxNumJibCranes: 'q[number_of_jib_cranes_lteq]',
  maxReadyCapacity: 'q[max_ready_crane_capacity_gteq]',
  maxReadySpan: 'q[max_ready_crane_span_lteq]',
  minAcresStabilized: 'q[min_yard_gteq]',
  minBridgeCraneSpan: 'q[max_bridge_crane_span_gteq]',
  minJibCraneSpan: 'q[jib_crane_span_gteq]',
  minNumBridgeCranes: 'q[number_of_bridge_cranes_gteq]',
  minNumJibCranes: 'q[number_of_jib_cranes_gteq]',
  minReadyCapacity: 'q[min_ready_crane_capacity_lteq]',
  minReadySpan: 'q[max_ready_crane_span_gteq]',
  ready: 'q[ready_eq]',
  served: 'q[served_eq]',
  servedRailProvider: 'q[served_rail_provider_eq]'
};

export const CHANGE_RECORDS = 'CHANGE_RECORDS';
export function changeRecords(properties, meta) {
  return {
    properties,
    meta,
    type: CHANGE_RECORDS
  };
}

export const RETRIEVE_RECORDS_STARTED = 'RETRIEVE_RECORDS_STARTED';
export function retrieveRecordsStarted() {
  return { type: RETRIEVE_RECORDS_STARTED };
}

export const RETRIEVE_RECORDS_ENDED = 'RETRIEVE_RECORDS_ENDED';
export function retrieveRecordsEnded() {
  return { type: RETRIEVE_RECORDS_ENDED };
}

export function retrieveRecords() {
  return (dispatch, getState) => {
    dispatch(retrieveRecordsStarted());
    const { coordinates, paths, query, scope } = getState();
    const filterOptions = retrieveFilterOptions(query, scope);
    const ransackQuery = new RansackQuery(
      filterOptions,
      { ...keys, ...saleAndLeaseKeys, ...industrialKeys, ...multifamilyKeys }
    ).toRansack();

    let path = paths.properties;
    const coordinatesQuery = isEmpty(coordinates.userBounds) ? coordinates.mapBounds : coordinates.userBounds;
    if (!isEmpty(coordinatesQuery)) {
      path = `${path}?${stringify(snakecaseKeys({ coordinates: coordinatesQuery }), { nested: true })}`;
    }

    // TODO: Use `get` from 'helpers/api', then:
    //       Get rid of all the camelcase/snakecase conversions in propertyFromApi
    //       Get rid of metaFromApi entirely
    return axios.get(path, { params: { ...ransackQuery, ...getCustomFieldQueryParams(getState) } })
      .then((response) => {
        const properties = _.map(response.data.properties, record => propertyFromApi(record));
        const meta = metaFromApi(response.data.meta);
        setItem('researchPropertiesIds', meta.propertyIds);
        dispatch(hideMapInfoBubble());
        return dispatch(changeRecords(properties, meta));
      });
  };
}

function getCustomFieldQueryParams(getState) {
  const state = getState();
  return buildCustomFieldQueryParams(
    state.static.customFields,
    state.query.customFieldFilters || {}
  );
}

export function downloadPropertiesAsCsv() {
  return (_, getState) => {
    const { paths, query, scope, selectList } = getState();
    const filterOptions = retrieveFilterOptions(query, scope);
    const ransackQuery = new RansackQuery(
      filterOptions,
      { ...keys, ...saleAndLeaseKeys, ...industrialKeys, ...multifamilyKeys }
    ).toRansack();
    const flatParams = { ...ransackQuery, selected: selectList.selectedPropertyIds };
    const nestedParams = postify(flatParams);
    axios.post(paths.propertiesToCsvParams, nestedParams)
      .then((response) => {
        window.location.assign(`${paths.propertiesToCsv}?paramsKey=${response.data.key}`);
      });
  };
}

export function delayRetrieveRecords() {
  return dispatch => debouncedActionCreators.retrieveRecords(dispatch);
}

function retrieveFilterOptions(query, scope) {
  keys.maxLotSize = (query.propertyFilter.lotUnits === 'sf') ? 'q[lot_size_sf_lteq]' : 'q[lot_size_lteq]';
  keys.minLotSize = (query.propertyFilter.lotUnits === 'sf') ? 'q[lot_size_sf_gteq]' : 'q[lot_size_gteq]';

  const leaseRate = leaseRateMappings[query.saleFilter.leaseRateUnits];
  keys.maxLeasePrice =  leaseRate.max;
  keys.minLeasePrice =  leaseRate.min;

  const {
    maxSalesDate,
    minSalesDate,
    maxLeaseDate,
    minLeaseDate,
    maxLeasePrice,
    minLeasePrice,
    ...saleFilters
  } = { ...query.saleFilter };

  const { driveThroughAccess, served, ready, ...industrialFilters } = query.industrialFilter;
  const { cities, currentCity, ...addressFilter } = query.addressFilter;
  const filterOptions = {
    ...addressFilter,
    ...query.propertyFilter,
    ...industrialFilters,
    ...saleFilters,
    ...query.multifamilyFilter
  };

  if (driveThroughAccess) {
    filterOptions.driveThroughAccess = driveThroughAccess;
  }

  if (served) {
    filterOptions.served = served;
  }

  if (ready) {
    filterOptions.ready = ready;
  }

  if (maxLeasePrice > 0) {
    filterOptions.maxLeasePrice = maxLeasePrice * leaseRate.conversion;
  }
  if (minLeasePrice > 0) {
    filterOptions.minLeasePrice = minLeasePrice * leaseRate.conversion;
  }

  if (minLeaseDate) {
    filterOptions.minLeaseDate = moment(minLeaseDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  }

  if (maxLeaseDate) {
    filterOptions.maxLeaseDate = moment(maxLeaseDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  }

  if (minSalesDate) {
    filterOptions.minSalesDate = moment(minSalesDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  }

  if (maxSalesDate) {
    filterOptions.maxSalesDate = moment(maxSalesDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  }

  if (currentCity) {
    filterOptions.cities = [...cities, currentCity];
  } else {
    filterOptions.cities = cities;
  }

  if (query.associationFilters.hasActiveDeals) {
    filterOptions.minActiveDealCount = 1;
  }

  if (query.associationFilters.hasActiveEdgeProjects) {
    filterOptions.minActiveEdgeProjectCount = 1;
  }

  filterOptions.availabilities = query.availabilities;
  filterOptions.assetClasses = propertyTypeParams(query.assetClasses);
  filterOptions.buildingClasses = query.buildingClasses;
  filterOptions.excludeTags = query.excludeTags;
  filterOptions.investmentTypes = query.investmentTypes;
  filterOptions.searchText = query.searchText;
  filterOptions.propertyPage = query.propertyPage;
  filterOptions.propertySort = formatPropertySort(query.propertySort);
  filterOptions.researchGroupIds = query.researchGroupIds;
  filterOptions.tags = query.tags;
  filterOptions.databaseId = scope.databaseId;
  return filterOptions;
}

const formatPropertySort = (sorting) => {
  const [field, direction] = sorting.split(' ');
  switch (field) {
  case 'name': {
    return [`name ${direction}`, `address ${direction}`];
  }
  case 'numberOfUnits': {
    return `number_of_units ${direction}`;
  }
  case 'size': {
    return `building_size ${direction}`;
  }
  case 'lotSize': {
    return `lot_size ${direction}`;
  }
  case 'yearBuilt': {
    return `year_built ${direction}`;
  }
  case 'buildingClass': {
    return `building_class ${direction}`;
  }
  case 'yearRenovated': {
    return `renovated ${direction}`;
  }
  case 'createdAt': {
    return `created_at ${direction}`;
  }
  case 'createdBy': {
    return [
      `version_metadata_created_by_first_name ${direction}`,
      `version_metadata_created_by_last_name ${direction}`
    ];
  }
  case 'lastUpdatedAt': {
    return `version_metadata_last_updated_at ${direction}`;
  }
  case 'lastUpdatedBy': {
    return [
      `version_metadata_last_updated_by_first_name ${direction}`,
      `version_metadata_last_updated_by_last_name ${direction}`
    ];
  }
  default:
    return `${field} ${direction}`;
  }
};

function metaFromApi(apiMeta) {
  return {
    currentPage: apiMeta.current_page,
    pageSize: apiMeta.page_size,
    totalCount: apiMeta.total_count,
    totalPages: apiMeta.total_pages,
    mapLocations: apiMeta.map_locations,
    propertyIds: apiMeta.property_ids,
    databasePropertyCount: apiMeta.database_property_count,
    totalUngroupedCount: apiMeta.total_ungrouped_count
  };
}
